import type { ProductAttribute } from '@cobrastyle/shared-types';

/**
 * Turning Torsbo's Magento attributes into the two things the redesigned
 * product page shows: the six-cell quick-spec grid beside the price, and the
 * full "Specifikationer" table inside the accordion.
 *
 * Everything here reads `product.attributes`, which the adapter already builds
 * from `custom_attributesV2(filters: { is_visible_on_front: true })` on the
 * product query — so none of this costs an extra round trip.
 *
 * The reason this is a curated list rather than "take the first six": the
 * visible-on-front set differs completely between product kinds. A rifle
 * carries Kaliber/Piplängd/Gänga; a scope carries Förstoring/Tubdiameter/
 * Objektiv; a cleaning kit carries almost nothing. One priority order covering
 * both families lets each product show the first six facts it actually has,
 * and quietly show fewer when it has fewer.
 */

/** Values Magento uses for "no" across both its select options and its 0/1 flags. */
const NEGATIVE = /^(nej|no|0|false|-)$/i;

/**
 * Whether a yes/no attribute is set.
 *
 * Deliberately a negative list rather than a positive one: these attributes
 * arrive in two shapes — selects that yield "Ja"/"Nej" labels, and boolean
 * flags that yield "1"/"0" — and presence alone is never the answer, because
 * "Nej" is a present value meaning the opposite. `parseProductExtras` in
 * product-extras.ts applies the same rule to `th_vanster` for the card badge.
 */
export function isAffirmative(value: string | undefined | null): boolean {
  const trimmed = value?.trim();
  return !!trimmed && !NEGATIVE.test(trimmed);
}

/** The trimmed value of `code`, or undefined when absent or empty. */
export function attributeValue(
  attributes: ProductAttribute[],
  code: string,
): string | undefined {
  const value = attributes.find((attr) => attr.code === code)?.value?.trim();
  return value ? value : undefined;
}

/**
 * One cell of the quick-spec grid, or one row of the technical table.
 *
 * A label or value may be either Magento's own string or a translation key,
 * never both — derived facts ("Skick: Ny", which is inferred from the *absence*
 * of a used flag) have no Magento string to show, and Torsbo has attributes
 * whose Magento label is broken. The renderer resolves whichever is set.
 */
export interface ProductSpec {
  code: string;
  /** Magento's label, when usable as-is. */
  label?: string;
  /** Key under `product.spec`, when the label is derived or Magento's is broken. */
  labelKey?: string;
  /** Magento's value, when usable as-is. */
  value?: string;
  /** Key under `product.spec`, when the value is derived from a yes/no flag. */
  valueKey?: string;
}

interface SpecRule {
  code: string;
  /** Replaces Magento's label. */
  labelKey?: string;
  /**
   * Render as a derived fact rather than the raw "Ja"/"Nej". Omitting
   * `whenClear` drops the cell entirely for a "no" — right for a marker like
   * left-handedness, where "Utförande: Höger" would be an inference we cannot
   * actually make (an unset flag is not the same as a right-handed product).
   */
  flag?: { whenSet: string; whenClear?: string };
}

/**
 * The quick-spec order, most identifying fact first. Weapons lead, optics
 * follow, then the facts that apply to both.
 *
 * `th_varugrupp` is absent on purpose. Its labels used to be raw internal
 * codes ("1821", "P01") and printed gibberish; the attribute import fixed that,
 * and it now names the product group properly — which is exactly why it stays
 * out of this table. `productKind` puts it in the eyebrow above the title, so a
 * row here would only repeat it.
 */
const SPEC_PRIORITY: SpecRule[] = [
  { code: 'th_kaliber' },
  { code: 'th_vapentyp' },
  { code: 'th_piplangd' },
  // Magento has no label for this one — it returns the code itself.
  { code: 'th_ganga', labelKey: 'threadedBarrel' },
  { code: 'th_forstoring' },
  { code: 'th_tubdiameter' },
  { code: 'th_objektivdiameter' },
  { code: 'th_riktmedel' },
  {
    code: 'th_begagnad',
    labelKey: 'condition',
    flag: { whenSet: 'conditionUsed', whenClear: 'conditionNew' },
  },
  { code: 'th_vanster', labelKey: 'handedness', flag: { whenSet: 'handednessLeft' } },
  { code: 'th_totallangd' },
  { code: 'th_patron_antal' },
  { code: 'th_kolv_material' },
  { code: 'th_kolv_typ' },
  { code: 'th_modellserie' },
  { code: 'th_lada' },
];

/**
 * The same order as bare codes, for consumers that need the ranking without
 * the label and flag rules — the compare table sorts its rows by it.
 */
export const SPEC_PRIORITY_CODES: readonly string[] = SPEC_PRIORITY.map((rule) => rule.code);

/** The canvas draws six cells, in three rows of two. */
export const MAX_SPEC_CELLS = 6;

/** The first `limit` priority attributes this product actually carries. */
export function selectSpecs(
  attributes: ProductAttribute[],
  limit: number = MAX_SPEC_CELLS,
): ProductSpec[] {
  const specs: ProductSpec[] = [];

  for (const rule of SPEC_PRIORITY) {
    if (specs.length >= limit) break;

    const attribute = attributes.find((attr) => attr.code === rule.code);
    const raw = attribute?.value?.trim();
    if (!raw) continue;

    if (rule.flag) {
      const valueKey = isAffirmative(raw) ? rule.flag.whenSet : rule.flag.whenClear;
      if (!valueKey) continue;
      specs.push({ code: rule.code, label: attribute!.label, labelKey: rule.labelKey, valueKey });
      continue;
    }

    specs.push({ code: rule.code, label: attribute!.label, labelKey: rule.labelKey, value: raw });
  }

  return specs;
}

/** How many specs a fallback meta description spends characters on. */
const MAX_META_FACTS = 3;

/**
 * The quick-spec facts for a fallback meta description, as plain text.
 *
 * Same priority as the grid, but only specs whose label AND value are
 * Magento's own strings: a meta tag is built outside any translation context,
 * so a derived cell ("Skick: Ny", "Gängad pipa") is skipped rather than
 * printed as its key.
 */
export function metaFacts(attributes: ProductAttribute[]): { label: string; value: string }[] {
  return selectSpecs(attributes, Infinity)
    .filter((spec) => spec.label && spec.value && !spec.labelKey && !spec.valueKey)
    .slice(0, MAX_META_FACTS)
    .map((spec) => ({ label: spec.label!, value: spec.value! }));
}

/**
 * Attributes that are visible-on-front in Magento but are not readable facts:
 * an internal group code, a feed flag, and 0/1 booleans the page renders as its
 * own markers instead.
 *
 * `th_licenspliktig` is here on Torsbo's instruction (p.7 of the "Hemsidan 2.0"
 * feedback): the licence requirement already has a notice of its own above the
 * buy button and a paragraph in the delivery section, so repeating it as a
 * "Licenspliktig: Ja" table row says the same thing a third time — and reads as
 * a specification of the product rather than a condition of the sale.
 */
const NOISE_CODES = new Set([
  'th_varugrupp',
  'th_feed',
  'th_skickas_ej',
  'th_licenspliktig',
  // A raw SKU list the paket page resolves into the includes grid, never a
  // readable fact. Excluded explicitly rather than relying on the label rule
  // below: it is only unlabelled because nobody has ticked "Visible on Catalog
  // Pages on Storefront" for it, and that is one checkbox away from printing
  // "9000089,1615359,1010023M" as a specification.
  'th_paket_innehall',
]);

/** Magento labels known to be missing, mapped to a key under `product.spec`. */
const LABEL_FIXES = new Map([['th_ganga', 'threadedBarrel']]);

/**
 * Every readable attribute, for the accordion's full table.
 *
 * Unlike the quick grid this is not curated — new attributes staff add should
 * appear without a code change. It only drops the unreadable ones, including
 * any attribute whose label came back as its own code, which is how the adapter
 * signals that the store has no label for it.
 */
export function technicalSpecs(attributes: ProductAttribute[]): ProductSpec[] {
  return attributes
    .filter((attr) => attr.value?.trim() && !NOISE_CODES.has(attr.code))
    .filter((attr) => attr.label !== attr.code || LABEL_FIXES.has(attr.code))
    .map((attr) => ({
      code: attr.code,
      label: attr.label,
      labelKey: LABEL_FIXES.get(attr.code),
      value: attr.value.trim(),
    }));
}

/** `th_licenspliktig` — the product may only be handed over against a licence. */
export function requiresLicense(attributes: ProductAttribute[]): boolean {
  return isAffirmative(attributeValue(attributes, 'th_licenspliktig'));
}

/** `th_skickas_ej` — "Endast hämta i butik"; the product is never shipped. */
export function isPickupOnly(attributes: ProductAttribute[]): boolean {
  return isAffirmative(attributeValue(attributes, 'th_skickas_ej'));
}

/**
 * The kind of product, for the eyebrow above the title.
 *
 * Three sources, most specific first:
 *
 *  1. `th_vapentyp` — "Kulgevär", "Hagelgevär". Present on 2 348 products.
 *  2. `th_varugrupp` — "Mörkersikten", "Kolvar (Märkta)", "Hundpejl". Present
 *     on 4 887 of 4 888, so it answers almost everything the weapon type does
 *     not. This is p.13 of the "Hemsidan 2.0" feedback ("många artiklar får
 *     inte rätt varugrupps attribut"): a night-vision unit read
 *     "AVSTÅNDSMÄTARE", a rifle stock read "VAPEN" and a dog tracker read
 *     "JAKT", all of them the category talking rather than the product.
 *  3. The first category, as a last resort — for many products that is a brand
 *     or a campaign listing rather than a type, which is why it ranks last.
 */
export function productKind(
  attributes: ProductAttribute[],
  fallback?: string,
): string | undefined {
  return (
    attributeValue(attributes, 'th_vapentyp') ??
    attributeValue(attributes, 'th_varugrupp') ??
    fallback
  );
}
